import { useEffect, useRef, useState } from 'react';
import { Play, Pause, CheckCircle, Volume2, VolumeX, Maximize } from 'lucide-react';

interface VideoPlayerProps {
  videoUrl: string;
  videoId: string;
  duration: number;
  initialProgress: number;
  isCompleted: boolean;
  allowSkipping: boolean;
  minWatchPercentage: number;
  onProgressUpdate: (seconds: number, completed: boolean) => void;
  onManualComplete?: () => void;
}

export default function VideoPlayer({
  videoUrl,
  videoId,
  duration,
  initialProgress,
  isCompleted,
  allowSkipping,
  minWatchPercentage,
  onProgressUpdate,
  onManualComplete,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(initialProgress);
  const [maxWatchedTime, setMaxWatchedTime] = useState(initialProgress);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveTimeRef = useRef(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Set initial time
    video.currentTime = initialProgress;

    const handleTimeUpdate = () => {
      const current = video.currentTime;
      setCurrentTime(current);

      // Track maximum watched time to prevent skipping
      if (current > maxWatchedTime) {
        setMaxWatchedTime(current);
      }

      // Prevent skipping ahead if not allowed
      if (!allowSkipping && current > maxWatchedTime + 2) {
        video.currentTime = maxWatchedTime;
        video.pause();
        setIsPlaying(false);
      }
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      setIsPlaying(false);
      // Mark as complete when video ends
      onProgressUpdate(duration, true);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, [allowSkipping, maxWatchedTime, duration, initialProgress, onProgressUpdate]);

  // Auto-save progress every 5 seconds
  useEffect(() => {
    if (isPlaying) {
      progressIntervalRef.current = setInterval(() => {
        const now = Date.now();
        // Only save if at least 5 seconds have passed since last save
        if (now - lastSaveTimeRef.current >= 5000) {
          const watchPercentage = (currentTime / duration) * 100;
          const shouldComplete = watchPercentage >= minWatchPercentage;
          onProgressUpdate(Math.floor(currentTime), shouldComplete);
          lastSaveTimeRef.current = now;
          console.log(`Progress saved: ${Math.floor(currentTime)}s (${Math.round(watchPercentage)}%)`);
        }
      }, 5000);
    } else {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    }

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, currentTime, duration, minWatchPercentage, onProgressUpdate]);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = parseFloat(e.target.value);

    // Only allow seeking to already watched portions if skipping is disabled
    if (!allowSkipping && newTime > maxWatchedTime) {
      return;
    }

    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const watchedPercentage = duration > 0 ? (maxWatchedTime / duration) * 100 : 0;

  return (
    <div className="relative bg-black rounded-lg overflow-hidden group">
      {isCompleted && (
        <div className="absolute top-4 right-4 z-10 bg-emerald-500 text-white px-3 py-1 rounded-full flex items-center space-x-1 text-sm shadow-lg">
          <CheckCircle className="w-4 h-4" />
          <span>Completed</span>
        </div>
      )}

      <video
        ref={videoRef}
        className="w-full aspect-video"
        src={videoUrl}
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      />

      {/* Custom Controls */}
      <div
        className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 transition-opacity ${showControls ? 'opacity-100' : 'opacity-0'
          }`}
        onMouseEnter={() => setShowControls(true)}
      >
        {/* Progress Bar */}
        <div className="mb-3">
          <input
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer slider"
            style={{
              background: `linear-gradient(to right, #10b981 0%, #10b981 ${progressPercentage}%, #4b5563 ${progressPercentage}%, #4b5563 100%)`
            }}
          />
          <div className="flex justify-between text-white text-xs mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Play/Pause Button */}
            <button
              onClick={togglePlayPause}
              className="text-white hover:text-emerald-500 transition-colors"
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
            </button>

            {/* Volume Controls */}
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleMute}
                className="text-white hover:text-emerald-500 transition-colors"
              >
                {isMuted || volume === 0 ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={handleVolumeChange}
                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
              />
            </div>

            {/* Progress Info */}
            <div className="text-white text-sm">
              {Math.round(progressPercentage)}% watched
              {minWatchPercentage > 0 && (
                <span className="text-gray-400 ml-1">
                  ({minWatchPercentage}% required)
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Fullscreen Button */}
            <button
              onClick={toggleFullscreen}
              className="text-white hover:text-emerald-500 transition-colors"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Warnings */}
        <div className="mt-2 space-y-1">
          {!allowSkipping && (
            <div className="text-yellow-400 text-xs text-center">
              ⚠️ Skipping ahead is disabled for this video
            </div>
          )}
        </div>
      </div>

      {/* Manual Complete Button */}
      {!isCompleted && onManualComplete && (
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={async () => {
              setIsMarkingComplete(true);
              try {
                await onManualComplete();
              } finally {
                setIsMarkingComplete(false);
              }
            }}
            disabled={isMarkingComplete}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-colors flex items-center space-x-2 disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {isMarkingComplete ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Marking...</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                <span>Mark as Complete</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
